import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

type Task = {
  id: number;
  title: string;
  description: string;
  bonus_description: string;
  channel_url: string;
  completed: boolean;
  completed_at: string | null;
  bonus_expires_at: string | null;
};

type CheckInStatus = {
  can_check_in: boolean;
  current_streak: number;
  bonus_chests_remaining: number;
  last_check_in: string | null;
};

const TasksScreen = () => {
  const { t } = useLanguage();
  const userId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [checkInStatus, setCheckInStatus] = useState<CheckInStatus | null>(null);
  const [loading] = useState<boolean>(false); // setLoading не используется пока
  const [checkingIn, setCheckingIn] = useState<boolean>(false);
  const [checking, setChecking] = useState<number | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    loadTasks();
    loadCheckInStatus();
    // Обновляем задачи каждые 30 секунд
    const interval = setInterval(() => {
      loadTasks();
      loadCheckInStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCheckInStatus = async (): Promise<void> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/daily-checkin/status`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      setCheckInStatus(response.data);
    } catch (err: any) {
      console.error('Error loading check-in status:', err);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/daily-checkin`, null, {
        params: { wallet_address: userId },
        timeout: 30000
      });

      if (response.data.success) {
        setSuccess(response.data.is_reward_earned ? t.streakReward : response.data.message);
        await loadCheckInStatus();
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      console.error('Error checking in:', err);
      setError(err.response?.data?.detail || 'Ошибка при отметке');
    } finally {
      setCheckingIn(false);
    }
  };

  const loadTasks = async (retryCount = 3): Promise<void> => {
    try {
      console.log('📡 Загрузка задач...', { userId, retryCount });
      
      // ✅ ЧИТАЕМ referrer_id из URL или localStorage
      const webApp = (window as any)?.Telegram?.WebApp;
      let startParam = webApp?.initDataUnsafe?.start_param;
      
      if (!startParam && window.location.hash) {
        const match = window.location.hash.match(/tgWebAppStartParam=(\d+)/);
        if (match) {
          startParam = match[1];
        }
      }
      
      // Сохраняем в localStorage если есть
      if (startParam && !localStorage.getItem('referrer_id')) {
        localStorage.setItem('referrer_id', startParam);
        console.log('💾 referrer_id сохранен в localStorage:', startParam);
      }
      
      // Читаем из localStorage если не было в URL
      const referrerId = startParam || localStorage.getItem('referrer_id');
      
      const params: any = { wallet_address: userId };
      if (referrerId) {
        params.referrer_id = parseInt(referrerId);
        console.log('🔗 Передаем referrer_id в /tasks:', referrerId);
      }
      
      const response = await axios.get(`${API_BASE_URL}/tasks`, {
        params,
        timeout: 30000
      });
      console.log('✅ Задачи загружены:', response.data);
      setTasks(response.data.tasks || []);
    } catch (err: any) {
      console.error('❌ Ошибка загрузки задач:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        retryCount
      });
      
      // Повторная попытка при таймауте или сетевых ошибках
      if (retryCount > 0 && (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || !err.response)) {
        console.log(`🔄 Повторная попытка через 2 сек... (осталось попыток: ${retryCount})`);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadTasks(retryCount - 1);
      }
      
      setTasks([]);
    }
  };

  const handleOpenChannel = (channelUrl: string) => {
    // Открываем канал в новой вкладке
    window.open(channelUrl, '_blank');
  };

  const handleCheckSubscription = async (taskId: number) => {
    setChecking(taskId);
    setError('');
    setSuccess('');

    try {
      // Проверяем подписку на бэкенде
      const response = await axios.post(`${API_BASE_URL}/tasks/complete`, {
        wallet_address: userId,
        task_id: taskId
      }, { timeout: 30000 });

      if (response.data.completed) {
        setSuccess(t.taskCompleted || 'Задача выполнена!');
        await loadTasks();
      } else {
        setError(t.subscribeFirst || 'Сначала подпишись на канал');
      }
    } catch (err: any) {
      console.error('Error checking subscription:', err);
      setError(err.response?.data?.detail || 'Ошибка проверки подписки');
    } finally {
      setChecking(null);
    }
  };

  const formatTimeRemaining = (expiresAt: string): string => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return '00:00:00';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const isBonusExpired = (expiresAt: string | null): boolean => {
    if (!expiresAt) return true;
    const now = new Date();
    const expires = new Date(expiresAt);
    return expires.getTime() - now.getTime() <= 0;
  };

  const activeTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 pb-20 px-4 pt-6">
      {/* Header */}
      <div className="card bg-gradient-to-br from-indigo-700/80 to-purple-800/80 text-white text-center mb-4">
        <div className="text-3xl font-bold mb-2">{t.tasksTitle}</div>
        <div className="text-sm opacity-90">{t.subscribeBonus}</div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="card bg-red-600/30 border border-red-500 text-white mb-4">
          <div className="font-semibold">❌ {error}</div>
        </div>
      )}

      {success && (
        <div className="card bg-green-600/30 border border-green-500 text-white mb-4">
          <div className="font-semibold">✅ {success}</div>
        </div>
      )}

      {/* Daily Check-In */}
      {checkInStatus && (
        <div className="card bg-gradient-to-br from-yellow-100 to-orange-100 border-2 border-yellow-500 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 flex-shrink-0 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-3xl">
              📅
            </div>

            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1 text-yellow-800">{t.dailyCheckIn}</h3>
              <p className="text-sm text-gray-700 mb-3">{t.dailyCheckInDesc}</p>
              
              {/* Progress */}
              <div className="bg-white/60 rounded-lg p-3 mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">{t.currentStreak}:</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {checkInStatus.current_streak} {t.daysStreak}
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-300 rounded-full h-3 mb-2">
                  <div 
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 h-3 rounded-full transition-all"
                    style={{ width: `${(checkInStatus.current_streak / 7) * 100}%` }}
                  ></div>
                </div>
                
                <p className="text-xs text-center text-gray-600">{t.reward7Days}</p>
              </div>

              {/* Bonus Chests */}
              {checkInStatus.bonus_chests_remaining > 0 && (
                <div className="bg-green-500/20 border border-green-600 rounded-lg p-2 mb-3 text-center">
                  <div className="text-xs font-semibold text-gray-700">{t.bonusChests}:</div>
                  <div className="text-xl font-bold text-green-700">
                    {checkInStatus.bonus_chests_remaining} 🎁
                  </div>
                  <div className="text-xs text-gray-600">-35% времени открытия</div>
                </div>
              )}
              
              {/* Check-In Button */}
              <button
                onClick={handleCheckIn}
                disabled={!checkInStatus.can_check_in || checkingIn}
                className={`w-full py-3 px-4 rounded-lg font-bold transition-all ${
                  !checkInStatus.can_check_in || checkingIn
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                    : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg'
                }`}
              >
                {!checkInStatus.can_check_in 
                  ? t.alreadyCheckedIn 
                  : checkingIn 
                  ? '⏳ Отмечаемся...' 
                  : `✨ ${t.checkInButton}`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Available Tasks */}
      {activeTasks.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white mb-3">{t.availableTasks}</h2>
          {activeTasks.map((task) => (
            <div key={task.id} className="card bg-white/90 mb-4">
              <div className="flex items-start gap-4">
                {/* Channel Avatar - кликабельная */}
                <div 
                  className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-yellow-400 to-orange-500 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => handleOpenChannel(task.channel_url)}
                >
                  <img
                    src="/assets/channel-avatar.jpg"
                    alt="Channel"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-1">{t.subscribeToChannel}</h3>
                  <p className="text-sm text-gray-600 mb-3">{t.subscribeBonus}</p>
                  
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleOpenChannel(task.channel_url)}
                      className="w-full py-2 px-4 rounded-lg font-bold bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white transition-all"
                    >
                      📢 {t.openChannel || 'Подписаться'}
                    </button>
                    
                    <button
                      onClick={() => handleCheckSubscription(task.id)}
                      disabled={loading || checking === task.id}
                      className={`w-full py-2 px-4 rounded-lg font-bold transition-all ${
                        checking === task.id
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {checking === task.id ? (t.processing || 'Проверка...') : t.checkSubscription}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Completed Tasks with Active Bonus */}
      {completedTasks.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-white mb-3">{t.completedTasks}</h2>
          {completedTasks.map((task) => {
            const bonusExpired = isBonusExpired(task.bonus_expires_at);
            
            return (
              <div 
                key={task.id} 
                className={`card mb-4 ${
                  bonusExpired 
                    ? 'bg-gray-100/90 border-2 border-gray-400 cursor-pointer hover:opacity-80 transition-opacity' 
                    : 'bg-green-100/90 border-2 border-green-500'
                }`}
                onClick={() => bonusExpired && handleOpenChannel(task.channel_url)}
              >
                <div className="flex items-start gap-4">
                  {/* Channel Avatar - кликабельная */}
                  <div 
                    className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-yellow-400 to-orange-500 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenChannel(task.channel_url);
                    }}
                  >
                    <img
                      src="/assets/channel-avatar.jpg"
                      alt="Channel"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>

                  <div className="flex-1">
                    <h3 className={`font-bold text-lg mb-1 ${bonusExpired ? 'text-gray-600' : 'text-green-700'}`}>
                      {bonusExpired ? '✔️ Задача выполнена' : `✅ ${t.subscribeToChannel}`}
                    </h3>
                    <p className="text-sm text-gray-700 mb-2">
                      {bonusExpired ? 'Бонус истек' : t.bonusActive}
                    </p>
                    
                    {!bonusExpired && task.bonus_expires_at && (
                      <div className="bg-yellow-400/30 border border-yellow-600 rounded-lg p-2 text-center">
                        <div className="text-xs font-semibold text-gray-700">{t.bonusExpires}</div>
                        <div className="text-lg font-bold font-mono text-gray-900">
                          {formatTimeRemaining(task.bonus_expires_at)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {t.subscribeBonusShort}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tasks.length === 0 && !loading && (
        <div className="card text-center text-gray-600">
          <div className="text-4xl mb-2">📋</div>
          <div>Нет доступных задач</div>
        </div>
      )}
    </div>
  );
};

export default TasksScreen;

